import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { formatDistanceStrict } from 'date-fns';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { customerSelector, useCreateFakePIXPaymentMutation } from '@/api/v1';
import Copy from '@/assets/icons/copy.svg?react';
import { LabeledAmount } from '@/components/molecules/LabeledAmount';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { PIXQRCode } from '@/components/molecules/PIXQRCode';
import { SHOULD_MOCK_DEPOSITS } from '@/constants';
import { useProcessedTxAmounts } from '@/hooks/useProcessedTxAmounts';
import { useToast } from '@/hooks/useToast';
import { useTransactionNotification } from '@/hooks/useTransactionNotification';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { getCustomerTaxId } from '@/types/customer';
import { Locale } from '@/types/locale';
import {
  IntegratorOperation,
  StatusType,
  TransactionNotification,
  TransactionResponse,
} from '@/types/transaction';
import { isPixInLocalInfo } from '@/types/transaction.ext';
import { toDateFnsLocale } from '@/utility/toDateFnsLocale';

interface Props {
  readonly transaction: TransactionResponse;
  readonly successPath: string;
}

export const PIXQRCodeModalContent: React.FC<Props> = (props: Props): React.ReactElement => {
  const toast = useToast();
  const tr = useTranslator();
  const locale = React.useContext<Locale>(LocaleContext);

  const { data: customer } = useSelector(customerSelector);
  const { transaction, successPath } = props;

  const [createFakePIXTransaction, mutationResult] = useCreateFakePIXPaymentMutation();
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [isCopying, setIsCopying] = React.useState<boolean>(false);

  React.useEffect((): void => {
    switch (mutationResult.status) {
      case QueryStatus.rejected:
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        break;
      default:
        // Ignore
        break;
    }
  }, [mutationResult.status, toast, tr]);

  const location = useLocation();
  const navigate = useNavigate();
  const notifications = useTransactionNotification(transaction?.id ?? '');
  const pixKey = React.useMemo((): string | null => {
    if (!transaction) {
      return null;
    }

    const { localInfo } = transaction;
    if (isPixInLocalInfo(localInfo)) {
      return localInfo.pixCopyAndPaste;
    } else {
      return null;
    }
  }, [transaction]);

  const copyToClipboard = React.useCallback(async (): Promise<void> => {
    const { clipboard } = navigator;

    if (pixKey !== null) {
      setIsCopying(true);
      try {
        await clipboard.writeText(pixKey);
        // If we're in the dev environment, let's pretend a PIX payment was mad
        if (SHOULD_MOCK_DEPOSITS) {
          const taxId = getCustomerTaxId(customer);

          console.debug(`creating fake pix transaction for ${taxId}`);
          createFakePIXTransaction({
            type: 'PIX',
            transactionId: transaction.id,
            documentId: taxId,
          });
        }
        toast.show(tr('LINK_COPIED'), 'success');
      } catch {
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
      } finally {
        setIsCopying(false);
      }
    }
  }, [pixKey, toast, tr, customer, createFakePIXTransaction, transaction.id]);

  const { fromAmount, fromCurrency, toAmount, toCurrency } = useProcessedTxAmounts(transaction);

  const visiblePixString = React.useMemo((): string => {
    if (pixKey === null) {
      return '';
    } else if (expanded) {
      return pixKey;
    } else if (pixKey.length > 70) {
      return pixKey.slice(0, 70) + '\u2026';
    } else {
      return pixKey;
    }
  }, [expanded, pixKey]);

  const expand = React.useCallback((): void => {
    setExpanded(true);
  }, []);

  React.useEffect((): void => {
    const deposit = notifications.find(
      (notification: TransactionNotification): boolean =>
        notification.operation === IntegratorOperation.PIX_DEPOSIT_RECEIVED,
    );

    if (deposit?.status === StatusType.SUCCEEDED) {
      const search = new URLSearchParams(location.search);

      search.set('transactionId', transaction.id);
      navigate({
        pathname: successPath,
        search: search.toString(),
      });
    }
  }, [location.search, navigate, notifications, successPath, transaction.id]);

  const expirationFormatted = React.useMemo((): string | null => {
    const { localInfo } = transaction;
    if (!isPixInLocalInfo(localInfo)) {
      return null;
    }

    const expirationDate = new Date(localInfo.pixExpiresAt);

    if (isNaN(expirationDate.getTime())) {
      console.error('Invalid pixExpiresAt provided:', localInfo.pixExpiresAt);
      return null;
    }

    const now = new Date();

    if (expirationDate.getTime() <= now.getTime()) {
      return null;
    }

    return formatDistanceStrict(expirationDate, now, {
      unit: 'hour',
      roundingMethod: 'ceil',
      locale: toDateFnsLocale(locale),
    });
  }, [locale, transaction]);

  return (
    <Paper elevation={1}>
      <Box display="flex" flexDirection="column" gap={3} py={3} px={4}>
        <Box sx={styles.codeContainer}>
          <Box sx={styles.codeQR}>
            <PIXQRCode content={pixKey} />
          </Box>
          <Box sx={styles.codeStringAndButtonContainer}>
            <Box sx={styles.codeStringBox}>
              <Typography component="div" fontSize={14} fontWeight={400} lineHeight="24px">
                <Typography
                  sx={styles.codeString}
                  fontSize="inherit"
                  fontWeight="inherit"
                  lineHeight="inherit">
                  {visiblePixString}
                </Typography>
                {!expanded ? (
                  <Typography
                    sx={styles.seeAllLink}
                    component="a"
                    color="custom.LightGray"
                    fontSize="inherit"
                    fontWeight="inherit"
                    lineHeight="inherit"
                    onClick={expand}>
                    {tr('SEE_ALL')}
                  </Typography>
                ) : (
                  <></>
                )}
              </Typography>
            </Box>
            <LoadingButton
              variant="dark"
              size="compact"
              startIcon={<Copy fill="currentColor" />}
              disabled={pixKey === null}
              loading={isCopying}
              onClick={copyToClipboard}>
              {tr('COPY_LINK')}
            </LoadingButton>
          </Box>
        </Box>
        <Box>
          <LabeledAmount label={tr('YOU_SEND')} amount={fromAmount} currency={fromCurrency} />
          <LabeledAmount label={tr('YOU_RECEIVE')} amount={toAmount} currency={toCurrency} />
        </Box>
        {expirationFormatted && (
          <Box textAlign="center">
            <Typography fontSize={16} fontWeight={400}>
              <Typography fontSize="inherit" fontWeight="inherit" component="span">
                {tr('QUOTE_WILL_EXPIRE')}{' '}
              </Typography>
              <Typography
                fontSize="inherit"
                fontWeight="inherit"
                component="span"
                color="primary.main">
                {expirationFormatted}
              </Typography>
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const styles: Record<string, SystemStyleObject> = {
  codeContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    backgroundColor: 'custom.Primary1Background',
    padding: 4,
    gap: 3,
    borderRadius: 3,
  },
  seeAllLink: {
    cursor: 'pointer',
  },
  codeString: {
    whiteSpace: 'wrap',
    overflowWrap: 'break-word',
    wordBreak: 'break-all',
    overflow: 'hidden',
  },
  codeStringBox: {
    mb: 2,
  },
  codeStringAndButtonContainer: {
    minWidth: 0,
    flex: 1,
  },
  codeQR: {
    backgroundColor: 'primary.contrastText',
    flexShrink: 0,
    padding: 1,
    borderRadius: 2,
    width: 156,
    height: 156,
  },
};
